import React, { useEffect, useMemo, useState } from "react";
import SearchableDropdown from "../Subscription/SearchableDropdown";
import { useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import {
  loadIncentiveInsurances,
  loadIncentiveCptCodes,
  loadIncentiveFacilities,
} from "../../Services/IncentiveRateService";

const ALL_OPTION = { value: "ALL", label: "All" };

const buildOptions = (values = [], includeAll = true) => {
  const unique = Array.from(new Set((values || []).filter(Boolean)));
  const options = unique
    .filter((value) => value !== "ALL")
    .map((value) => ({ value, label: value }));
  return includeAll ? [ALL_OPTION, ...options] : options;
};

const validationSchema = Yup.object({
  organization_id: Yup.string().trim().required("Organization is required"),
  insurance: Yup.string().trim().required("Insurance is required"),
  cpt_code: Yup.string().trim().required("CPT code is required"),
  facility: Yup.string().trim().required("Facility is required"),
  standard_rate: Yup.number()
    .typeError("Standard rate must be a number")
    .required("Standard rate is required")
    .moreThan(0, "Standard rate must be greater than 0"),
});

export default function IncentiveRateFormModal({
  open,
  onClose,
  onSubmit,
  loading,
  initialValues = {},
  organizations = [],
  organizationsLoading = false,
  token,
  title = "Add Incentive Rate",
}) {
  const [insurances, setInsurances] = useState([]);
  const [cptCodes, setCptCodes] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const organizationOptions = useMemo(
    () =>
      organizations.map((org) => ({
        value: org.id ?? org.OrganizationId ?? org.organization_id ?? org.Id ?? org.value ?? '',
        label:
          org.group_office_name ??
          org.OrganizationName ??
          org.organization_name ??
          org.name ??
          org.label ??
          'Unknown Organization',
      })),
    [organizations],
  );

  const insuranceOptions = useMemo(() => buildOptions(insurances), [insurances]);
  const cptCodeOptions = useMemo(() => buildOptions(cptCodes, false), [cptCodes]);
  const facilityOptions = useMemo(() => buildOptions(facilities), [facilities]);

  const isEdit = Boolean(initialValues.id);

  const formik = useFormik({
    initialValues: {
      organization_id: initialValues.organization_id || "",
      insurance: initialValues.insurance || "ALL",
      cpt_code: initialValues.cpt_code || "",
      facility: initialValues.facility || "ALL",
      standard_rate: initialValues.standard_rate ?? "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        organization_id: values.organization_id,
        insurance: values.insurance || "ALL",
        cpt_code: (values.cpt_code || "").trim(),
        facility: values.facility || "ALL",
        standard_rate: Number(values.standard_rate),
      };
      if (initialValues.id) payload.id = initialValues.id;
      onSubmit(payload);
    },
  });

  const selectedOrganizationId = formik.values.organization_id;

  useEffect(() => {
    if (!open || !token || !selectedOrganizationId) {
      setInsurances([]);
      setCptCodes([]);
      setFacilities([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setOptionsLoading(true);
        const [insuranceRes, cptRes, facilityRes] = await Promise.all([
          loadIncentiveInsurances(token, selectedOrganizationId),
          loadIncentiveCptCodes(token, selectedOrganizationId),
          loadIncentiveFacilities(token, selectedOrganizationId),
        ]);
        if (cancelled) return;
        setInsurances(insuranceRes?.is_success ? insuranceRes.data || [] : []);
        setCptCodes(cptRes?.is_success ? cptRes.data || [] : []);
        setFacilities(facilityRes?.is_success ? facilityRes.data || [] : []);
      } catch (error) {
        if (!cancelled) {
          setInsurances([]);
          setCptCodes([]);
          setFacilities([]);
        }
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, token, selectedOrganizationId]);

  if (!open) return null;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(var(--hc-black-rgb), 0.25)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content rounded-4 shadow-lg">
          <div className="modal-header border-0 pb-2">
            <h5 className="modal-title fw-bold">{title}</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              disabled={loading}
            />
          </div>
          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body pt-0">
              <div className="mb-3">
                <SearchableDropdown
                  label="Organization"
                  placeholder="Select Organization"
                  options={organizationOptions}
                  value={formik.values.organization_id}
                  onChange={(value) => formik.setFieldValue("organization_id", value || "")}
                  disabled={organizationsLoading || loading || isEdit}
                  loading={organizationsLoading}
                  emptyMessage="No organizations found"
                  required
                />
                {formik.touched.organization_id && formik.errors.organization_id && (
                  <div className="invalid-feedback d-block">{formik.errors.organization_id}</div>
                )}
                {isEdit ? (
                  <div className="form-text">Organization can't be changed once a rate is created.</div>
                ) : null}
              </div>
              <div className="mb-3">
                <SearchableDropdown
                  label="Insurance"
                  placeholder={selectedOrganizationId ? "Select Insurance" : "Select an organization first"}
                  options={insuranceOptions}
                  value={formik.values.insurance}
                  onChange={(value) => formik.setFieldValue("insurance", value)}
                  disabled={!selectedOrganizationId || optionsLoading || loading}
                  loading={optionsLoading}
                  allowCustom
                  emptyMessage="No insurances found"
                  required
                />
                {formik.touched.insurance && formik.errors.insurance && (
                  <div className="invalid-feedback d-block">{formik.errors.insurance}</div>
                )}
              </div>
              <div className="mb-3">
                <SearchableDropdown
                  label="CPT Code"
                  placeholder={selectedOrganizationId ? "Select CPT Code" : "Select an organization first"}
                  options={cptCodeOptions}
                  value={formik.values.cpt_code}
                  onChange={(value) => formik.setFieldValue("cpt_code", value)}
                  disabled={!selectedOrganizationId || optionsLoading || loading}
                  loading={optionsLoading}
                  allowCustom
                  emptyMessage="No CPT codes found"
                  required
                />
                {formik.touched.cpt_code && formik.errors.cpt_code && (
                  <div className="invalid-feedback d-block">{formik.errors.cpt_code}</div>
                )}
                <div className="form-text">
                  Use composed codes like <span className="fw-semibold">H2000-HO</span> to price a specific modifier, or the bare code to cover every modifier.
                </div>
              </div>
              <div className="mb-3">
                <SearchableDropdown
                  label="Facility"
                  placeholder={selectedOrganizationId ? "Select Facility" : "Select an organization first"}
                  options={facilityOptions}
                  value={formik.values.facility}
                  onChange={(value) => formik.setFieldValue("facility", value)}
                  disabled={!selectedOrganizationId || optionsLoading || loading}
                  loading={optionsLoading}
                  allowCustom
                  emptyMessage="No facilities found"
                  required
                />
                {formik.touched.facility && formik.errors.facility && (
                  <div className="invalid-feedback d-block">{formik.errors.facility}</div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Standard Rate ($) <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className={`form-control rounded-3${formik.touched.standard_rate && formik.errors.standard_rate ? ' is-invalid' : ''}`}
                  name="standard_rate"
                  value={formik.values.standard_rate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  step="0.01"
                  min="0"
                  placeholder="e.g. 151.53"
                  disabled={loading}
                />
                {formik.touched.standard_rate && formik.errors.standard_rate && (
                  <div className="invalid-feedback">{formik.errors.standard_rate}</div>
                )}
              </div>
            </div>
            <div className="modal-footer border-0 pt-0 d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary px-4 rounded-3"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn hc-bg-primary text-white px-4 rounded-3 fw-semibold d-flex align-items-center gap-2"
                disabled={loading}
              >
                {loading && (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                )}
                {title.includes("Edit") ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

IncentiveRateFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  initialValues: PropTypes.object,
  organizations: PropTypes.array,
  organizationsLoading: PropTypes.bool,
  token: PropTypes.string,
  title: PropTypes.string,
};
